import { create } from "zustand";
import { fontApi } from "../services/font-api";
import type { FontOption, FontState, GoogleFont, GoogleFontRaw } from "../types";

// Map variant names to numeric weights
const VARIANT_TO_WEIGHT: Record<string, string> = {
	thin: "100",
	hairline: "100",
	extralight: "200",
	"extra-light": "200",
	ultralight: "200",
	light: "300",
	regular: "400",
	normal: "400",
	medium: "500",
	semibold: "600",
	"semi-bold": "600",
	demibold: "600",
	bold: "700",
	extrabold: "800",
	"extra-bold": "800",
	ultrabold: "800",
	black: "900",
	heavy: "900",
};

/**
 * Extract numeric weight from a variant string
 * e.g., "regular" -> "400", "700" -> "700", "700italic" -> "700"
 */
function extractWeight(variant: string): string | null {
	const normalized = variant.toLowerCase();

	// Check if it starts with a number (e.g., "700", "700italic")
	const numericMatch = normalized.match(/^(\d+)/);
	if (numericMatch) {
		return numericMatch[1];
	}

	// Check if it's a named weight (e.g., "regular", "bold")
	// Remove "italic" suffix if present
	const withoutItalic = normalized.replace(/italic$/, "").trim();
	if (VARIANT_TO_WEIGHT[withoutItalic]) {
		return VARIANT_TO_WEIGHT[withoutItalic];
	}

	// Check the original (for "regular", "bold" without italic)
	if (VARIANT_TO_WEIGHT[normalized]) {
		return VARIANT_TO_WEIGHT[normalized];
	}

	return null;
}

/**
 * Transform raw API font data to processed format with numeric weights
 */
function transformFont(raw: GoogleFontRaw): GoogleFont {
	const weights = new Set<string>();

	for (const variant of raw.variants) {
		const weight = extractWeight(variant);
		if (weight) {
			weights.add(weight);
		}
	}

	// Sort weights numerically
	const sortedWeights = Array.from(weights).sort((a, b) => parseInt(a) - parseInt(b));

	return {
		family: raw.family,
		category: raw.category,
		variants: sortedWeights,
	};
}

// Fallback fonts to use when API fails
const FALLBACK_FONTS: GoogleFont[] = [
	{ family: "Inter", category: "sans-serif", variants: ["300", "400", "500", "600", "700", "800"] },
	{ family: "Roboto", category: "sans-serif", variants: ["300", "400", "500", "700"] },
	{ family: "Open Sans", category: "sans-serif", variants: ["300", "400", "500", "600", "700", "800"] },
	{ family: "Lato", category: "sans-serif", variants: ["300", "400", "700"] },
	{ family: "Montserrat", category: "sans-serif", variants: ["300", "400", "500", "600", "700", "800"] },
	{ family: "Poppins", category: "sans-serif", variants: ["300", "400", "500", "600", "700", "800"] },
	{ family: "Playfair Display", category: "serif", variants: ["400", "500", "600", "700", "800"] },
	{ family: "Source Sans Pro", category: "sans-serif", variants: ["300", "400", "600", "700"] },
	{ family: "Nunito", category: "sans-serif", variants: ["300", "400", "500", "600", "700", "800"] },
	{ family: "Merriweather", category: "serif", variants: ["300", "400", "700"] },
	{ family: "Oswald", category: "sans-serif", variants: ["300", "400", "500", "600", "700"] },
	{ family: "Raleway", category: "sans-serif", variants: ["300", "400", "500", "600", "700", "800"] },
	{ family: "PT Sans", category: "sans-serif", variants: ["400", "700"] },
	{ family: "Ubuntu", category: "sans-serif", variants: ["300", "400", "500", "700"] },
	{ family: "Noto Sans", category: "sans-serif", variants: ["300", "400", "500", "600", "700", "800"] },
];

// Categories suitable for heading fonts (display-oriented)
const HEADING_CATEGORIES = ["display", "sans-serif", "serif"];

// Categories suitable for body fonts (readability-focused)
const BODY_CATEGORIES = ["sans-serif", "serif"];

export const useFontStore = create<FontState>((set, get) => ({
	fonts: [],
	isLoading: false,
	error: null,
	lastFetched: null,

	fetchFonts: async () => {
		const state = get();

		// Skip if already loading
		if (state.isLoading) {
			return state.fonts.length > 0 ? state.fonts : null;
		}

		// Skip if we have fonts and they were fetched recently (within 1 hour)
		const ONE_HOUR = 60 * 60 * 1000;
		if (state.fonts.length > 0 && state.lastFetched && Date.now() - state.lastFetched < ONE_HOUR) {
			return state.fonts;
		}

		set({ isLoading: true, error: null });

		try {
			const result = await fontApi.getFonts();

			if (result.success && result.data) {
				// Transform raw fonts to processed format with numeric weights
				const processedFonts = result.data.map(transformFont);

				set({
					fonts: processedFonts,
					isLoading: false,
					lastFetched: Date.now(),
				});
				return processedFonts;
			}

			// API failed, use fallback fonts
			console.warn("Failed to fetch fonts from API, using fallback fonts");
			set({
				fonts: FALLBACK_FONTS,
				isLoading: false,
				error: result.message || "Failed to fetch fonts",
				lastFetched: Date.now(),
			});
			return FALLBACK_FONTS;
		} catch (error) {
			// Network error or other exception, use fallback fonts
			console.error("Error fetching fonts:", error);
			set({
				fonts: FALLBACK_FONTS,
				isLoading: false,
				error: "Failed to fetch fonts",
				lastFetched: Date.now(),
			});
			return FALLBACK_FONTS;
		}
	},

	getWeightsForFont: (fontFamily: string) => {
		const state = get();
		const fonts = state.fonts.length > 0 ? state.fonts : FALLBACK_FONTS;
		const font = fonts.find((f) => f.family === fontFamily);

		if (!font) {
			// Return common weights as fallback
			return ["400", "700"];
		}

		// Variants are already numeric and sorted
		return font.variants;
	},

	getHeadingFontOptions: () => {
		const state = get();
		const fonts = state.fonts.length > 0 ? state.fonts : FALLBACK_FONTS;

		return fonts
			.filter((font) => HEADING_CATEGORIES.includes(font.category))
			.map((font): FontOption => ({
				value: font.family,
				label: font.family,
				category: font.category,
				weights: font.variants,
			}));
	},

	getBodyFontOptions: () => {
		const state = get();
		const fonts = state.fonts.length > 0 ? state.fonts : FALLBACK_FONTS;

		return fonts
			.filter((font) => BODY_CATEGORIES.includes(font.category))
			.map((font): FontOption => ({
				value: font.family,
				label: font.family,
				category: font.category,
				weights: font.variants,
			}));
	},
}));
